import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';
import { adminJson } from '../utils/adminAuth';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);

export default function AdminBilling() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [dealers, setDealers] = useState([]);
  const [dealerId, setDealerId] = useState('');
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(today());
  const [invoice, setInvoice] = useState(null);
  const [recon, setRecon] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const authHeader = useCallback(() => {
    const token = localStorage.getItem('adminAuth');
    if (!token) { navigate('/admin/login'); return null; }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  useEffect(() => {
    const h = authHeader();
    if (!h) return;
    fetch(`${API_BASE}/admin/billing/dealers`, { headers: h })
      .then(adminJson)
      .then(d => setDealers(Array.isArray(d) ? d : []))
      .catch(() => setError('Failed to load dealers'));
  }, [authHeader]);

  const loadInvoice = useCallback(() => {
    const h = authHeader();
    if (!h || !dealerId) return;
    setError(''); setRecon(null); setBusy('invoice');
    fetch(`${API_BASE}/admin/billing/our-invoice?dealer_id=${dealerId}&start=${start}&end=${end}`, { headers: h })
      .then(adminJson).then(setInvoice)
      .catch(() => setError('Failed to load invoice')).finally(() => setBusy(''));
  }, [authHeader, dealerId, start, end]);

  const generate = () => {
    const h = authHeader();
    if (!h || !dealerId) return;
    setError(''); setBusy('generate');
    fetch(`${API_BASE}/admin/billing/generate?dealer_id=${dealerId}&start=${start}&end=${end}&commit=true`,
      { method: 'POST', headers: h })
      .then(adminJson)
      .then(() => loadInvoice())
      .catch(() => setError('Generation failed')).finally(() => setBusy(''));
  };

  const uploadCompetitorInvoice = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const h = authHeader();
    if (!h || !dealerId) return;
    setError(''); setBusy('reconcile');
    const fd = new FormData();
    fd.append('file', f);
    fetch(`${API_BASE}/admin/billing/reconcile?dealer_id=${dealerId}&start=${start}&end=${end}`,
      { method: 'POST', headers: h, body: fd })
      .then(adminJson).then(setRecon)
      .catch(() => setError('Reconcile failed'))
      .finally(() => { setBusy(''); if (fileRef.current) fileRef.current.value = ''; });
  };

  // ⭐ EXPORT. Kim, 2026-09-17: picked a date range, left the dealer blank, and looked for
  // a way to export everything in that range -- there was no export control on this page at
  // all. It could only ever answer one rooftop at a time, because it was built around
  // diffing a single competitor invoice. Dealer is OPTIONAL here: blank means every dealer.
  //
  // Goes through fetch + blob rather than a plain href because the endpoint is admin-gated
  // and needs the Authorization header; a bare link would arrive unauthenticated and
  // download an error page as a .csv, which is worse than failing.
  const exportCsv = () => {
    const h = authHeader();
    if (!h) return;
    setBusy('export');
    const q = new URLSearchParams({ start, end });
    if (dealerId) q.set('dealer_id', dealerId);
    fetch(`${API_BASE}/admin/billing/export?${q}`, { headers: h })
      .then(r => { if (!r.ok) throw new Error('export failed'); return r.blob(); })
      .then(b => {
        const url = URL.createObjectURL(b);
        const a = document.createElement('a');
        a.href = url;
        a.download = `billing_${start}_to_${end}${dealerId ? '' : '_all'}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => setError('Export failed'))
      .finally(() => setBusy(''));
  };

  const dealerName = dealers.find(d => String(d.id) === String(dealerId))?.dealership_name || '';

  return (
    <div style={{ padding: 20, maxWidth: 1150, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} className="no-print">
        <h2>Billing Reconciliation</h2>
      </div>
      <p style={{ color: '#6b7280', fontSize: 13, marginTop: 0 }} className="no-print">
        Double-shoot validation: generate our shadow invoice from app captures, then upload the competitor
        invoice to compare VIN-by-VIN. The dealer never receives this — it's internal validation + photographer pay.
      </p>
      {error && <div style={{ color: '#dc2626', marginBottom: 8 }}>{error}</div>}

      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', margin: '12px 0' }} className="no-print">
        <label style={lbl}>Dealer
          <select value={dealerId} onChange={e => { setDealerId(e.target.value); setInvoice(null); setRecon(null); }} style={ctrl}>
            <option value="">All dealers</option>
            {dealers.map(d => <option key={d.id} value={d.id}>{d.dealership_name} ({d.dealer_code})</option>)}
          </select>
        </label>
        <label style={lbl}>Start<input type="date" value={start} onChange={e => setStart(e.target.value)} style={ctrl} /></label>
        <label style={lbl}>End<input type="date" value={end} onChange={e => setEnd(e.target.value)} style={ctrl} /></label>
        <button onClick={loadInvoice} disabled={!dealerId || busy} style={btn}>{busy === 'invoice' ? '…' : 'Load invoice'}</button>
        <button onClick={generate} disabled={!dealerId || busy} style={btnLight}>{busy === 'generate' ? 'Generating…' : 'Generate from captures'}</button>
        <button onClick={exportCsv} disabled={!!busy} style={btnLight}>
          {busy === 'export' ? 'Exporting…' : `⤓ Export CSV${dealerId ? '' : ' (all dealers)'}`}
        </button>
        <button onClick={() => window.print()} disabled={!invoice} style={btnLight}>🖨 Print</button>
      </div>

      {/* Our invoice */}
      {invoice && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <h3 style={{ margin: 0 }}>Our shadow invoice — {dealerName}</h3>
            <div style={{ fontSize: 13, color: '#6b7280' }}>{invoice.start} → {invoice.end}</div>
          </div>
          <div style={{ display: 'flex', gap: 24, margin: '10px 0' }}>
            <Stat label="Line items" value={invoice.count ?? 0} />
            <Stat label="Bill total" value={money(invoice.bill_total)} accent="#0078D4" />
            <Stat label="Photographer pay" value={money(invoice.pay_total)} accent="#059669" />
          </div>
          {invoice.count > 0 ? (
            <table style={table}>
              <thead><tr><th style={th}>VIN</th><th style={th}>Service</th><th style={th}>Cond</th>
                <th style={th}>Photographer</th><th style={th}>Date</th><th style={thR}>Bill</th><th style={thR}>Pay</th></tr></thead>
              <tbody>
                {invoice.lines.map((l, i) => (
                  <tr key={i}>
                    <td style={tdMono}>{l.vin}</td><td style={td}>{l.description}</td><td style={td}>{l.condition}</td>
                    <td style={td}>{l.photographer || '—'}</td><td style={td}>{l.visit_date}</td>
                    <td style={tdR}>{money(l.labor_price)}</td><td style={tdR}>{money(l.payout_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div style={{ color: '#6b7280', padding: 8 }}>No line items yet — click “Generate from captures”.</div>}
        </div>
      )}

      {/* competitor upload + reconcile */}
      {invoice && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} className="no-print">
            <h3 style={{ margin: 0 }}>Compare to competitor invoice</h3>
            <label style={{ ...btn, cursor: 'pointer' }}>
              {busy === 'reconcile' ? 'Comparing…' : '⬆ Upload competitor invoice (PDF)'}
              <input ref={fileRef} type="file" accept=".pdf,.csv" onChange={uploadCompetitorInvoice} style={{ display: 'none' }} />
            </label>
          </div>

          {recon && (
            <div>
              <div style={{ display: 'flex', gap: 14, margin: '12px 0', flexWrap: 'wrap' }}>
                <Stat label="Our total" value={money(recon.our_total)} accent="#0078D4" />
                <Stat label="Competitor total" value={money(recon.competitor_total)} accent="#7c3aed" />
                <Stat label="Difference" value={money(recon.our_total - recon.competitor_total)}
                      accent={Math.abs(recon.our_total - recon.competitor_total) < 0.005 ? '#059669' : '#dc2626'} />
                <Stat label="✅ Matched" value={recon.counts.matched} />
                <Stat label="💲 Mismatch" value={recon.counts.mismatch} accent={recon.counts.mismatch ? '#dc2626' : '#059669'} />
                <Stat label="⚠ Ours only" value={recon.counts.ours_only} accent={recon.counts.ours_only ? '#d97706' : '#059669'} />
                <Stat label="⚠ Competitor only" value={recon.counts.competitor_only} accent={recon.counts.competitor_only ? '#d97706' : '#059669'} />
              </div>
              {recon.counts.matched + recon.counts.mismatch + recon.counts.ours_only + recon.counts.competitor_only === 0 && (
                <div style={{ color: '#6b7280' }}>No VINs on either side for this period.</div>)}

              {recon.mismatch.length > 0 && (
                <Section title="💲 Amount mismatch (same VIN, different price)">
                  <table style={table}><thead><tr><th style={th}>VIN</th><th style={thR}>Ours</th><th style={thR}>Competitor</th><th style={thR}>Diff</th></tr></thead>
                    <tbody>{recon.mismatch.map((m, i) => (
                      <tr key={i}><td style={tdMono}>{m.vin}</td><td style={tdR}>{money(m.ours)}</td><td style={tdR}>{money(m.competitor)}</td>
                        <td style={{ ...tdR, color: '#dc2626' }}>{money(m.diff)}</td></tr>))}</tbody></table>
                </Section>)}
              {recon.ours_only.length > 0 && (
                <Section title="⚠ Billed by us, not on the competitor invoice">
                  <table style={table}><thead><tr><th style={th}>VIN</th><th style={th}>Service</th><th style={thR}>Bill</th></tr></thead>
                    <tbody>{recon.ours_only.map((m, i) => (
                      <tr key={i}><td style={tdMono}>{m.vin}</td><td style={td}>{m.desc}</td><td style={tdR}>{money(m.ours)}</td></tr>))}</tbody></table>
                </Section>)}
              {recon.competitor_only.length > 0 && (
                <Section title="⚠ On the competitor invoice, not billed by us">
                  <table style={table}><thead><tr><th style={th}>VIN</th><th style={th}>Description</th><th style={thR}>Competitor</th></tr></thead>
                    <tbody>{recon.competitor_only.map((m, i) => (
                      <tr key={i}><td style={tdMono}>{m.vin}</td><td style={td}>{m.desc}</td><td style={tdR}>{money(m.competitor)}</td></tr>))}</tbody></table>
                </Section>)}
              {recon.matched.length > 0 && (
                <Section title={`✅ Matched (${recon.matched.length})`}>
                  <div style={{ color: '#6b7280', fontSize: 13 }}>
                    {recon.matched.map(m => m.vin).join(', ')}
                  </div>
                </Section>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div><div style={{ fontSize: 20, fontWeight: 700, color: accent || '#111827' }}>{value}</div>
    <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div></div>
);
const Section = ({ title, children }) => (
  <div style={{ margin: '14px 0' }}><div style={{ fontWeight: 600, marginBottom: 6 }}>{title}</div>{children}</div>
);

const lbl = { display: 'flex', flexDirection: 'column', fontSize: 12, color: '#6b7280', gap: 3 };
const ctrl = { padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 };
const btn = { padding: '7px 14px', background: '#0078D4', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, cursor: 'pointer' };
const btnLight = { padding: '7px 14px', background: '#fff', color: '#0078D4', border: '1px solid #0078D4', borderRadius: 6, fontSize: 14, cursor: 'pointer' };
const linkBtn = { background: 'none', border: 'none', color: '#0078D4', cursor: 'pointer', fontSize: 14 };
const card = { border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginTop: 14, background: '#fff' };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 6 };
const th = { textAlign: 'left', padding: '7px 10px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' };
const thR = { ...th, textAlign: 'right' };
const td = { padding: '6px 10px', borderBottom: '1px solid #f1f5f9' };
const tdR = { ...td, textAlign: 'right' };
const tdMono = { ...td, fontFamily: 'monospace', fontSize: 12 };
